import React, { useState } from "react";
import { Updater } from "use-immer";

import { IgDivider, IgTypography } from "@intelligems/ig-theme";
import { GaAccount, GoogleGlobalSettingsDto } from "@intelligems/ig-types";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import theme from "../../../../theme";

import { GoogleAnalyticsProperty } from "./GoogleAnalyticsProperty";

interface GoogleAnalyticsAccountProps {
  account: GaAccount;
  index: number;
  globalSettings: GoogleGlobalSettingsDto;
  setGlobalSettings: Updater<GoogleGlobalSettingsDto>;
}

const PREFIX = "GaAccountSelect";

const classes = {
  accordian: `${PREFIX}-accordian`,
  expandMoreIcon: `${PREFIX}-expandMoreIcon`,
  mainText: `${PREFIX}-mainText`,
  iconMargin: `${PREFIX}-iconMargin`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.accordian}`]: {
    "&.MuiAccordion-root": {
      borderRadius: "15px",
    },
    "& .MuiDivider-root": {
      marginTop: "0px !important",
    },
  },
  [`& .${classes.expandMoreIcon}`]: {
    color: theme.palette.disabled.main,
  },
  [`& .${classes.mainText}`]: {
    fontSize: "18px",
    letterSpacing: "0.5px",
    fontWeight: 600,
  },
  [`& .${classes.iconMargin}`]: {
    marginRight: "7px",
    marginLeft: "7px",
  },
}));

export const GoogleAnalyticsAccount = ({
  account,
  index,
  globalSettings,
  setGlobalSettings,
}: GoogleAnalyticsAccountProps) => {
  const [expanded, setExpanded] = useState<boolean>(
    account.account === globalSettings.ga?.accountId
  );

  return (
    <StyledGrid item xs={12}>
      <Accordion
        className={classes.accordian}
        elevation={0}
        disableGutters
        onChange={() => setExpanded((prev) => !prev)}
        expanded={expanded}
        sx={{
          border:
            account.account === globalSettings.ga?.accountId
              ? `1px solid ${theme.palette.primary.main}`
              : "1px solid rgba(0, 0, 0, .125)",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className={classes.expandMoreIcon} />}
          aria-controls={`account-${account.account}-${index}`}
          id={`account-${account.account}-${index}`}
        >
          <Grid item>
            <IgTypography igStyle={"semibold"} sx={{ fontSize: "18px" }}>
              Account {index + 1}: {account.displayName} (
              {(account.account || "").split("/").pop()})
            </IgTypography>
          </Grid>
        </AccordionSummary>
        <IgDivider spacing={"s"} />
        <AccordionDetails sx={{ paddingBottom: "0px" }}>
          {(account.propertySummaries || []).map((property, index) => (
            <div key={index}>
              <GoogleAnalyticsProperty
                enabled={expanded}
                account={account}
                property={property}
                globalSettings={globalSettings}
                setGlobalSettings={setGlobalSettings}
              />
              {index !== (account.propertySummaries || []).length - 1 && (
                <Grid item>
                  <IgDivider spacing={"s"} />
                </Grid>
              )}
            </div>
          ))}
        </AccordionDetails>
      </Accordion>
    </StyledGrid>
  );
};
